import {
  getInitialData,
  scwFetch,
  setContainerHeader,
  dynamicContainer,
  clearDynamicContainer,
  clearContainerElement,
  debounce,
  handleError,
  showErrorPage,
  ScwError,
  errorLabelFragment,
} from "../main";
import { broadcastSsoStateChange } from "../utils/broadcast";
import { onSubmit } from "../utils/form";
import { showContainerLoader } from "../utils/loader";

interface ClientSelectionState {
  state: string;
  loginStringList: string[];
}

interface ClientSelectionResponse {
  redirectUrl?: string;
  redirectUrlAfterCallback?: string;
}

const initialData = getInitialData<ClientSelectionState>();
const { state } = initialData;
const loginStringList = [...initialData.loginStringList].sort((a, b) =>
  a.localeCompare(b, undefined, { ignorePunctuation: true }),
);

const templates = {
  clientListButton: document.getElementById(
    "template_clientListButton",
  ) as HTMLTemplateElement,
  clientForm: document.getElementById(
    "template_clientForm",
  ) as HTMLTemplateElement,
};

const selectClientAndRedirect = async (
  loginString: string,
  notify = true,
): Promise<boolean> => {
  const loginChallenge = localStorage.getItem("login_challenge");
  const refreshOneFound = await scwFetch<ClientSelectionResponse>(
    "/api/external-idp/" + state + "/client-selection",
    {
      method: "POST",
      body: { login_string: loginString, login_challenge: loginChallenge },
      notify,
    },
  );

  if (!refreshOneFound?.redirectUrl) return false;

  if (refreshOneFound.redirectUrlAfterCallback) {
    broadcastSsoStateChange({
      action: "SSO_LOGIN",
      payload: { url: refreshOneFound.redirectUrlAfterCallback },
    });
  }

  window.location.replace(refreshOneFound.redirectUrl);
  return true;
};

const selectClientAndRedirectIfValid = async (
  formData: FormData,
  key: string,
  errorContainer?: HTMLElement | null,
) => {
  clearContainerElement(errorContainer);

  const value = (formData.get(key) as string)?.toLocaleLowerCase();
  if (loginStringList.includes(value)) return selectClientAndRedirect(value);

  console.warn("Invalid client selection attempt:", value);

  // A typo deserves to be visible. A tampered submitter value lands here too, which is
  // why the console warning stays alongside the message.
  if (errorContainer) {
    const label = errorLabelFragment?.content?.cloneNode(
      true,
    ) as DocumentFragment;
    const span = label?.querySelector("span.scw-error-label") as HTMLSpanElement;
    if (span) {
      span.textContent = "We don't recognise that company name.";
      errorContainer.append(label);
    }
  }
};

const renderSelectionUI = () => {
  clearDynamicContainer();

  setContainerHeader({
    title: "Client Selection",
    subTitle: "Select your preferred login method",
  });

  const formFragment = templates.clientForm?.content?.cloneNode(
    true,
  ) as DocumentFragment;
  if (!formFragment) return;

  const buttonListForm = formFragment.querySelector(
    "form#selectByList",
  ) as HTMLFormElement;

  loginStringList.forEach((loginString) => {
    let buttonFragment = templates.clientListButton?.content?.cloneNode(
      true,
    ) as DocumentFragment;
    let button = buttonFragment.querySelector(
      'button[form="selectByList"]',
    ) as HTMLButtonElement;
    if (button) {
      button.textContent = loginString;
      button.setAttribute("value", loginString);
      buttonListForm.append(buttonFragment);
    }
  });

  const selectManualForm = formFragment.querySelector(
    "form#selectManual",
  ) as HTMLFormElement;

  // Same convention as change-password: the error container is scoped to the input
  // group rather than found by sibling traversal.
  const manualErrorContainer = selectManualForm.querySelector(
    "div.error-container",
  ) as HTMLDivElement;

  const manualSubmitButton = formFragment.querySelector(
    'button[form="selectManual"]',
  ) as HTMLButtonElement;

  onSubmit(
    selectManualForm,
    async (formData) => {
      await selectClientAndRedirectIfValid(
        formData,
        "login_string",
        manualErrorContainer,
      );
    },
    manualSubmitButton,
  );

  const selectByListForm = formFragment.querySelector(
    "form#selectByList",
  ) as HTMLFormElement;
  onSubmit(selectByListForm, async (formData) => {
    await selectClientAndRedirectIfValid(formData, "client");
  });

  const textInput = selectManualForm.querySelector(
    "input#login_string",
  ) as HTMLInputElement;
  textInput?.addEventListener(
    "input",
    debounce((e) => {
      const buttons = selectByListForm.querySelectorAll(
        'button[data-scw-button="list-item"]',
      );
      const searchKey = e.target.value?.toLocaleLowerCase() ?? "";

      buttons.forEach((btn) => {
        const ok = searchKey?.length
          ? btn?.textContent
              ?.toLocaleLowerCase()
              ?.includes(searchKey?.toLocaleLowerCase() ?? "")
          : true;

        btn.classList.toggle("scw-hidden", !ok);
      });
    }, 200),
  );

  dynamicContainer.append(formFragment);
};

const autoSelectSingleClient = async (
  loginString: string,
): Promise<boolean> => {
  setContainerHeader({
    title: "Signing you in",
    subTitle: `Connecting you to ${loginString}…`,
  });
  showContainerLoader(dynamicContainer, "Signing you in…");

  try {
    if (await selectClientAndRedirect(loginString, false)) return true;

    console.warn("Client selection returned no redirect for:", loginString);
  } catch (err) {
    if (
      err instanceof ScwError &&
      err.statusCode !== null &&
      err.statusCode < 500
    ) {
      showErrorPage(err);
      return true;
    }

    if (!(err instanceof ScwError)) {
      console.error("Client auto-selection failed:", err);
    }
    handleError(err as Error);
  }

  return false;
};

const initClientSelection = async () => {
  if (loginStringList.length === 1) {
    if (await autoSelectSingleClient(loginStringList[0])) return;
  }
  
  renderSelectionUI();
};

initClientSelection();
